import React, { Component } from "react";
import { Button, Icon } from "@blueprintjs/core";
import { MultiSelect } from "@blueprintjs/select";
import { observable, action } from "mobx";
import { observer } from "mobx-react";
import PropTypes from "prop-types";
import { Api } from "services/Api";

// Skills are free-form, so the suggestions are simply every skill already in
// use in this tenant. Anything not on the list can still be typed in.
@observer
class SkillsPicker extends Component {
  @observable availableSkills = [];
  @observable query = "";

  componentDidMount() {
    this.loadSkills();
  }

  @action
  setQuery(query) {
    this.query = query;
  }

  @action
  onSkillsLoad(response) {
    this.availableSkills = (response.data.skills || []).filter(Boolean);
  }

  loadSkills() {
    Api.get("/users/skills")
      .then(this.onSkillsLoad.bind(this))
      .catch(() => {
        this.availableSkills = [];
      });
  }

  get selectedSkills() {
    return (this.props.skills || []).slice();
  }

  emit(skills) {
    const cleaned = [];
    skills.forEach((skill) => {
      const value = String(skill).trim();
      if (value.length > 0 && !cleaned.includes(value)) {
        cleaned.push(value);
      }
    });
    this.props.onChange(cleaned);
  }

  onItemSelect = (skill) => {
    this.setQuery("");
    const selected = this.selectedSkills;
    if (selected.some((item) => item.toLowerCase() === skill.toLowerCase())) {
      this.emit(selected.filter((item) => item !== skill));
    } else {
      this.emit([...selected, skill]);
    }
  };

  onRemove = (_tag, index) => {
    const selected = this.selectedSkills;
    this.emit(selected.filter((_item, itemIndex) => itemIndex !== index));
  };

  filterSkill = (query, skill) => {
    return skill.toLowerCase().includes(query.trim().toLowerCase());
  };

  isChosen(skill) {
    return this.selectedSkills.some((item) => item.toLowerCase() === skill.toLowerCase());
  }

  // One skill: a checkbox (filled when chosen) and its name. Clicking toggles it.
  renderSkill = (skill, { handleClick, modifiers }) => {
    if (!modifiers.matchesPredicate) {
      return null;
    }
    const chosen = this.isChosen(skill);
    return (
      <li key={skill}>
        <div
          role="option"
          aria-selected={chosen}
          className={`skills-menu__option${modifiers.active ? " is-active" : ""}${chosen ? " is-chosen" : ""}`}
          onClick={handleClick}
        >
          <span className="skills-menu__check" aria-hidden="true">
            {chosen && <Icon icon="small-tick" iconSize={14} />}
          </span>
          <span className="skills-menu__name">{skill}</span>
        </div>
      </li>
    );
  };

  renderCreateSkill = (query, active, handleClick) => (
    <li>
      <div
        role="option"
        aria-selected={false}
        className={`skills-menu__option skills-menu__create${active ? " is-active" : ""}`}
        onClick={handleClick}
      >
        <span className="skills-menu__check skills-menu__check--add" aria-hidden="true">
          <Icon icon="plus" iconSize={12} />
        </span>
        <span className="skills-menu__name">
          Add <strong>{`“${query.trim()}”`}</strong> as a new skill
        </span>
      </div>
    </li>
  );

  // The menu: a line on what to do (or how many match), the skills, and
  // "Add … as a new skill" when the typed text isn't one yet.
  renderMenu = ({ filteredItems, itemsParentRef, query, renderItem, renderCreateItem }) => {
    const typed = query.trim();
    const rows = filteredItems.map(renderItem).filter((row) => row != null);
    let status;
    if (!typed) {
      status = rows.length > 0 ? "Pick a skill, or type a new one" : "No skills yet. Type one to add it.";
    } else if (rows.length > 0) {
      status = `${rows.length} ${rows.length === 1 ? "skill matches" : "skills match"} “${typed}”`;
    } else {
      status = `No skill matches “${typed}”`;
    }
    const create = typed ? renderCreateItem() : null;
    return (
      <div className="skills-menu">
        <div className="skills-menu__status">{status}</div>
        {(rows.length > 0 || create) && (
          <ul className="skills-menu__list" ref={itemsParentRef} role="listbox" aria-label="Skills">
            {rows}
            {create}
          </ul>
        )}
      </div>
    );
  };

  createSkillFromQuery = (query) => query.trim();

  render() {
    const selected = this.selectedSkills;
    return (
      <MultiSelect
        className="skills-picker"
        items={this.availableSkills.slice()}
        selectedItems={selected}
        query={this.query}
        onQueryChange={(query) => this.setQuery(query)}
        itemRenderer={this.renderSkill}
        itemListRenderer={this.renderMenu}
        itemPredicate={this.filterSkill}
        itemsEqual={(a, b) => a.toLowerCase() === b.toLowerCase()}
        onItemSelect={this.onItemSelect}
        createNewItemFromQuery={this.createSkillFromQuery}
        createNewItemRenderer={this.renderCreateSkill}
        tagRenderer={(skill) => skill}
        tagInputProps={{
          onRemove: this.onRemove,
          placeholder: this.props.placeholder || "Add a skill...",
          rightElement:
            selected.length > 0 ? (
              <Button
                icon="cross"
                minimal={true}
                onClick={() => this.emit([])}
              />
            ) : null,
        }}
        popoverProps={{ minimal: true, usePortal: true, popoverClassName: "skills-picker-menu" }}
        resetOnSelect={true}
        fill={true}
      />
    );
  }
}

SkillsPicker.propTypes = {
  skills: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

export default SkillsPicker;
